import React, { useState } from 'react'

import { User } from '@guiker/authentication-shared'
import {
  Box,
  OneColumnGridLayout,
  TextField,
  TwoColumnsGridLayout,
  useFormContext,
  useMutation,
} from '@guiker/react-framework'
import { optionalConcat, yup } from '@guiker/shared-framework'

import { useAdminAuthenticationApiClient, useAuthenticationContext, useTranslation } from '../../hooks'
import { VerifyEmailTextField } from './VerifyEmailTextField'

type VerifyUserProps = {
  prefix?: string
  readOnly?: boolean
  hasReadOnlyFields?: boolean
  showVerifyButton?: boolean
  numberOfColumns?: number
  isEditable?: boolean
  emailName?: string
  handleVerifyUser?: (user: User, email?: string) => void
  defaultValue?: {
    id?: string
    emailAddress?: string
    firstName?: string
    lastName?: string
  }
  hideNameFields?: boolean
}

export const useHandleVerifyUser = (args: { prefix?: string; emailName?: string } = {}) => {
  const { prefix, emailName } = args
  const { setValue } = useFormContext()

  return (user: User, email: string) => {
    const { emailAddress = email, firstName, lastName, id } = { ...user }
    const option = { shouldDirty: true }
    setValue(optionalConcat([prefix, emailName ?? 'emailAddress'], '.'), emailAddress ?? '', option)
    setValue(optionalConcat([prefix, 'firstName'], '.'), firstName ?? '', option)
    setValue(optionalConcat([prefix, 'lastName'], '.'), lastName ?? '', option)
    setValue(optionalConcat([prefix, 'id'], '.'), id ?? undefined, option)
  }
}

export const VerifyUser: React.FC<VerifyUserProps> = ({
  prefix,
  hasReadOnlyFields = false,
  handleVerifyUser,
  defaultValue = {},
  readOnly = false,
  numberOfColumns = 1,
  isEditable = false,
  emailName,
  hideNameFields = false,
}) => {
  const { t } = useTranslation(['common', 'office-payment'])
  const { user } = useAuthenticationContext()
  const adminApiClient = useAdminAuthenticationApiClient(user?.accessToken)

  const [{ id, emailAddress, firstName, lastName }, setUserState] = useState(defaultValue)

  const verifyEmail = (emailAddress: string) => {
    const isEmailValid = yup.string().email().isValidSync(emailAddress)
    if (emailAddress && isEmailValid) {
      fetchOneUserByEmailAddress(emailAddress)
    }
  }

  const clearUserData = () => {
    handleVerifyUser?.(null)
    setUserState({ id: '', emailAddress: '', firstName: '', lastName: '' })
  }

  const { mutate: fetchOneUserByEmailAddress, isLoading } = useMutation(
    (payload: string) =>
      adminApiClient.findOneUserByEmailAddress({ queryParams: { email: payload, exactMatch: true } }),
    {
      onSuccess: (user) => {
        handleVerifyUser?.(user)
        setUserState(user)
      },
      onError: (_, val) => {
        handleVerifyUser?.(null, val)
      },
    },
  )

  const isDisabled = hasReadOnlyFields || (!!id && !!emailAddress && !isEditable) || isLoading

  const isOneColumn = numberOfColumns === 1
  const ColumnType = isOneColumn ? OneColumnGridLayout : TwoColumnsGridLayout
  const gridProp = { 1: { md: { start: 'auto', span: 2 } } } as const

  return (
    <ColumnType perItemGridColumn={!isOneColumn ? gridProp : undefined}>
      <Box display='none'>
        <TextField type='hidden' name={prefix ? `${prefix}.id` : 'id'} defaultValue={id} />
      </Box>
      <VerifyEmailTextField
        defaultValue={emailAddress}
        prefix={prefix}
        emailName={emailName}
        verifyEmail={verifyEmail}
        clearUserData={clearUserData}
        isLoading={isLoading}
        readOnly={readOnly}
      />
      {!hideNameFields && (
        <>
          <TextField
            disabled={isDisabled}
            required={!isDisabled}
            readOnly={readOnly}
            name={prefix ? `${prefix}.firstName` : 'firstName'}
            label={t('common:user.firstName')}
            defaultValue={firstName}
          />
          <TextField
            disabled={isDisabled}
            required={!isDisabled}
            readOnly={readOnly}
            name={prefix ? `${prefix}.lastName` : 'lastName'}
            label={t('common:user.lastName')}
            defaultValue={lastName}
          />
        </>
      )}
    </ColumnType>
  )
}
